import { BasketItem } from '../../../store/slices/basketSlice';

const REPLACEMENT_LOG_KEY = 'replacementLog';

export type ReplacementEntry = {
  id: string;
  /** Stored whole so the swap can be undone, not just described. */
  from: BasketItem;
  toCartKey: string;
  toId: number;
  toTitle: string;
  toTitleUz: string;
  /** Появился вместе с английской локализацией; у старых записей его нет. */
  toTitleEn?: string;
  toImage: string;
  /**
   * Unit price of the replacement, so the summary can show what the swap did
   * to the total. Optional: entries written before this existed have none, and
   * the UI just omits the delta for them.
   */
  toPrice?: number;
};

/**
 * Swaps made while browsing for replacements, so the confirmation step can
 * show "this became that" — and roll a swap back if the user changes their
 * mind. Session-scoped on purpose: it's a record of one editing pass.
 */
// Entries written by an older build have a different shape; reading one would
// blow up at `entry.from.title`. Validate instead of trusting the store.
const isValidEntry = (entry: unknown): entry is ReplacementEntry => {
  if (!entry || typeof entry !== 'object') return false;

  const candidate = entry as Partial<ReplacementEntry>;

  return (
    typeof candidate.id === 'string' &&
    typeof candidate.toCartKey === 'string' &&
    !!candidate.from &&
    typeof candidate.from === 'object' &&
    typeof candidate.from.cartKey === 'string'
  );
};

export const getReplacementLog = (): ReplacementEntry[] => {
  try {
    const raw = sessionStorage.getItem(REPLACEMENT_LOG_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isValidEntry) : [];
  } catch {
    return [];
  }
};

const save = (entries: ReplacementEntry[]) =>
  sessionStorage.setItem(REPLACEMENT_LOG_KEY, JSON.stringify(entries));

export const appendReplacement = (entry: ReplacementEntry) =>
  save([...getReplacementLog(), entry]);

export const removeReplacement = (id: string) =>
  save(getReplacementLog().filter(entry => entry.id !== id));

export const clearReplacementLog = () =>
  sessionStorage.removeItem(REPLACEMENT_LOG_KEY);
